import { useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { creatorOffer, creatorStart } from "odyssey-shared";
import { createRoot } from "react-dom/client";
import "../ui/kit/styles.ts";
import "./login-screen.css";
import { say } from "../words.ts";
import { login, logoutEverywhere, nameCharacter, register, storeGrant, type Grant, type Naming, type Roster, type Session } from "./auth.ts";
import { LoginScreen } from "./login-screen.tsx";
import { fieldProblem, initialLoginState, loginReducer, makesCharacter, nameStepNames, type LoginEvent, type LoginField, type LoginState } from "./login-state.ts";
import { LookStep, lookStepWords, type EntryLooks } from "./look-step.tsx";
import { pageSeed } from "./look-preview/look-panel.tsx";
import { ASK_NOTHING, type Ask } from "./ask.ts";
import type { ConfirmLogoutEverywhere } from "./logout-everywhere.tsx";
import { logOutWithRetry, type RetryLogout } from "./logout-retry.tsx";
import { MaintenanceScreen } from "./maintenance-screen.tsx";
import { INITIAL_SERVER_VIEW, loginBlocked, serverPhase, soundHoldFor, type ServerView } from "./server-status.ts";
import { ServerStatusContext, type ServerStatusSource } from "./server-strip.tsx";
import type { StatusPoll } from "./status-poll.ts";
import { MusicScene } from "./audio/music.ts";
import { pageSound } from "./audio/page-sound.ts";
import { ReturningScreen } from "./returning-screen.tsx";
import { SelectFlow } from "./select.tsx";

/**
 * The login screen of the game page (P1.4-T2, P1.4-T18, P1.4-T20, P1.4-T26, P1.9-T12, P1.4-T6): the kit's
 * screen over the page, with a log in form and a register form, each asking for an email and a password, the
 * name step and character select, shown when the page has no session and no `?token=`. It is a screen over
 * the one page rather than a page of its own, so the static build keeps one entry point and a player who logs
 * out is one form away from playing again. The name step asks for what the account lacks, as its naming
 * grant's `needs` names: a new account's family name and first name together, the family name alone for an
 * account whose characters predate families, or the first name alone for a family with no character (ADR-065
 * decisions 3 and 4); a grant with no `needs`, from a gateway before families, is the one field for the
 * character's name (ADR-064 decision 3). A step that makes a character continues to the Look step, where the
 * look and the voice are chosen and the character is made (`look-step.tsx`, ADR-094 decision 23). A login answered with the account's characters shows character select (`select.tsx`), whose
 * choice is the session the screen resolves with; one from a gateway before character select, which lists
 * none, plays the account's first character, as it did. Beside the name step's and character select's Log
 * out, the only log out the page offers until the game menu (P1.9-T14), is Log out everywhere, which ends
 * every session of the account once the player confirms it (P1.4-T15).
 *
 * A tab that resumed a kept device opens on the returning prompt (`returning-screen.tsx`, P1.4-T7): Continue
 * goes on with the resumed grant as a login would, and Log out ends the device's keep and shows the log in
 * form. The log in form's "Keep me logged in" is posted with the login (ADR-075).
 *
 * `LoginScreen` draws a `LoginState` and `loginReducer` moves it; this module sends the requests, keeps the
 * tab's grant, turns each answer into an event and focuses the field a move asks for.
 */

/**
 * What a resume gave a tab that holds no grant: the grant, as a login's, and the account's email, which the
 * returning prompt shows.
 */
export interface Returning {
  readonly grant: Grant;
  readonly email: string;
}

/**
 * The refresh token the screen holds while it is up, which the page reads when another tab logs out, to ask
 * the gateway whether this tab's session is still live: the returning prompt's, the name step's or character
 * select's, or null on the log in and register forms.
 */
export interface HeldToken {
  refreshToken: string | null;
}

/**
 * The refresh token a grant carries.
 *
 * @param grant The grant.
 * @returns Its refresh token.
 */
function tokenOf(grant: Grant): string {
  return grant.kind === "playing" ? grant.session.refreshToken : grant.naming.refreshToken;
}

/**
 * What character select is shown for: the refresh token a login answered, the account's family name and its
 * characters.
 */
export interface Choosing {
  readonly refreshToken: string;
  readonly familyName: string;
  readonly roster: Roster;
}

/**
 * What the flow needs from the page: the account origin, the naming grant this tab holds, what a resume gave
 * it, the confirmation before logging out everywhere, where it writes the refresh token it holds, the dialog
 * for a logout that did not finish, the server's status poll (null on a page that runs none), the looks the
 * entry screens draw, and where the session goes once a character may play.
 */
interface LoginFlowProps {
  readonly base: string;
  readonly pending: Naming | null;
  readonly returning: Returning | null;
  readonly choosing: Choosing | null;
  readonly confirmEverywhere: ConfirmLogoutEverywhere;
  readonly ask: Ask;
  readonly held: HeldToken;
  readonly retryLogout: RetryLogout;
  readonly status: StatusPoll | null;
  readonly looks: EntryLooks;
  readonly onSession: (session: Session) => void;
}

/**
 * The state the screen opens in: the log in form, or the name step for the naming grant this tab holds, with
 * the fields its `needs` names.
 *
 * @param pending The naming grant this tab holds, or null.
 * @returns The state.
 */
function openingState(pending: Naming | null): LoginState {
  return pending === null ? initialLoginState("login") : initialLoginState("naming", pending.needs);
}

/**
 * Reloads the page: the maintenance page's Log in once the server is open again. A maintenance is when the game is
 * deployed, so the page that waited it out may be older than the server; the reload fetches the page now served,
 * and its login flow resumes a kept device without a password (ADR-075).
 */
function reloadPage(): void {
  window.location.reload();
}

/**
 * The maintenance page, or the account screens under the server's strip (ADR-078 decision 7). A page whose first
 * answer from the server says maintenance runs opens on the maintenance page, once; the strip's Details opens it
 * too, and its Log in, once the server is open again, reloads the page.
 *
 * @param props The page's half of the flow.
 * @returns The screen.
 */
function LoginFlow(props: LoginFlowProps): ReactElement {
  const { status } = props;
  const [statusPage, setStatusPage] = useState(false);
  const shownOnce = useRef(false);
  const view = useSyncExternalStore(status?.subscribe ?? noSubscription, status?.getSnapshot ?? initialView, initialView);
  const source = useMemo<ServerStatusSource | null>(() => (status === null ? null : { poll: status, onDetails: () => setStatusPage(true) }), [status]);

  useEffect(() => {
    if (view.opening === "maintenance" && !shownOnce.current) {
      shownOnce.current = true;
      setStatusPage(true);
    }
  }, [view.opening]);

  const phase = serverPhase(view, Date.now());
  useEffect(() => {
    const hold = soundHoldFor(phase);
    if (hold === "hold") {
      pageSound.maintenanceStart(false);
    } else if (hold === "release") {
      pageSound.maintenanceEnd();
    }
  }, [phase]);

  const paged = statusPage && status !== null;
  return (
    <>
      {paged ? (
        <MaintenanceScreen view={view} onLogIn={reloadPage} />
      ) : null}
      <div hidden={paged}>
        <ServerStatusContext.Provider value={source}>
          <AccountFlow {...props} view={view} covered={paged} />
        </ServerStatusContext.Provider>
      </div>
    </>
  );
}

/**
 * Subscribes to nothing, for a page that runs no status poll.
 *
 * @returns A no-op unsubscribe.
 */
function noSubscription(): () => void {
  return () => undefined;
}

/**
 * The view of a page that runs no status poll.
 *
 * @returns The initial view.
 */
function initialView(): ServerView {
  return INITIAL_SERVER_VIEW;
}

/**
 * The account screens under the server's strip: the returning prompt, character select, or the login screen's forms
 * and the requests behind them. While a request is out the flow refuses another send, a mode switch and Log out
 * everywhere, as the page did before the kit; `waiting` answers that at once, before React has drawn the busy
 * button, so a second click in the same frame sends nothing. Log in and Create account are refused while the server
 * is offline or under maintenance (ADR-078 decision 3), and a send clears "The server is back.". Character select asks
 * for the select screen's music and every other account screen for the title's (ADR-085 decision 13). A password
 * shown on the log in or register form is hidden again when the maintenance page covers the screen, as it is on
 * every move that leaves the form (`loginReducer`).
 *
 * @param props The flow's props, the server's view, and whether the maintenance page covers the screens.
 * @returns The screen.
 */
function AccountFlow({ base, pending, returning, choosing: opening, confirmEverywhere, ask, held, retryLogout, status, looks, view, covered, onSession }: LoginFlowProps & { readonly view: ServerView; readonly covered: boolean }): ReactElement {
  const [state, dispatch] = useReducer(loginReducer, pending, openingState);
  const [choosing, setChoosing] = useState<Choosing | null>(opening);
  const [back, setBack] = useState<Returning | null>(returning);
  const [leaving, setLeaving] = useState(false);
  const naming = useRef<Naming | null>(pending);
  const waiting = useRef(false);
  const email = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);
  const familyName = useRef<HTMLInputElement>(null);
  const characterName = useRef<HTMLInputElement>(null);
  const confirm = useRef<HTMLInputElement>(null);
  const inputs: Readonly<Record<LoginField, typeof email>> = { email, password, confirm, familyName, characterName };

  useEffect(() => {
    if (state.focus !== null) {
      inputs[state.focus].current?.focus();
    }
  }, [state]);

  useEffect(() => {
    if (covered) {
      dispatch({ type: "hide" });
    }
  }, [covered]);

  useEffect(() => {
    held.refreshToken = back !== null ? tokenOf(back.grant) : (choosing?.refreshToken ?? naming.current?.refreshToken ?? null);
  });

  /**
   * Starts a request: the flow waits and the screen shows busy on its submit button.
   *
   * @returns False when a request is already out, and nothing starts.
   */
  const begin = (): boolean => {
    if (waiting.current) {
      return false;
    }
    waiting.current = true;
    dispatch({ type: "sent" });
    return true;
  };

  /**
   * Ends the wait with the answer's move.
   *
   * @param event What the answer did to the screen.
   */
  const settle = (event: LoginEvent): void => {
    waiting.current = false;
    dispatch(event);
  };

  /**
   * Sends the form the screen is on, unless a field breaks the rule the gateway applies to it, which is
   * refused in its own note line with nothing sent (the form is `noValidate`, so the browser shows no bubble
   * of its own). A name step that makes a character first opens the Look step (ADR-094 decision 23) on a look the
   * creator picks, or the one chosen before, and sends nothing; the Look step's Enter the world sends the fields
   * the grant asks for, trimmed, with the voice and the look, and a refusal that names a field returns to the names
   * with the look kept. The family step alone sends its field at once. A login answered with a naming
   * grant stores the grant, so a reload returns to the name step; a name step answered that its session is
   * gone forgets the grant and returns to log in with the gateway's sentence, and any other refusal shows
   * under the field the gateway named, or under the step's last field.
   */
  const submit = (): void => {
    if (waiting.current) {
      return;
    }
    if (state.mode !== "naming" && status !== null && loginBlocked(view, Date.now())) {
      return;
    }
    status?.acknowledge();
    const problem = fieldProblem(state);
    if (problem !== null) {
      dispatch({ type: "invalid", field: problem });
      return;
    }
    if (state.mode === "naming" && state.step === "name" && makesCharacter(state)) {
      dispatch({ type: "looking", look: creatorStart(creatorOffer(looks.looks), null, pageSeed()) });
      return;
    }
    if (state.mode === "naming") {
      const held = naming.current;
      if (held === null || !begin()) {
        return;
      }
      const names = nameStepNames(state);
      void nameCharacter(base, held, names).then((outcome) => {
        if (outcome.ok) {
          onSession(names.look === undefined ? outcome.session : { ...outcome.session, look: names.look });
          return;
        }
        if (outcome.lost) {
          naming.current = null;
          storeGrant(null);
          settle({ type: "returned", text: outcome.message });
          return;
        }
        settle({ type: "refused", text: outcome.message, field: outcome.field });
      });
      return;
    }
    if (!begin()) {
      return;
    }
    const address = state.email.trim();
    if (state.mode === "register") {
      void register(base, { email: address, password: state.password }).then((outcome) => {
        settle(outcome.ok ? { type: "registered", text: say("account.registered"), email: address } : { type: "refused", text: outcome.message, field: outcome.field });
      });
      return;
    }
    void login(base, { identifier: address, password: state.password, keep: state.keep }).then((outcome) => {
      if (!outcome.ok) {
        settle({ type: "refused", text: outcome.message, field: null });
        return;
      }
      proceed(outcome.grant);
    });
  };

  /**
   * Goes on with a grant a login or a resume gave: a session with no roster, from a gateway before character
   * select, plays at once; one with the account's characters shows character select, storing nothing until a
   * character is chosen; and a naming grant is stored, so a reload returns to the name step, and moves to it.
   *
   * @param grant The grant.
   */
  const proceed = (grant: Grant): void => {
    if (grant.kind === "playing") {
      const { session, roster } = grant;
      if (roster === undefined) {
        onSession(session);
        return;
      }
      settle({ type: "selecting" });
      setChoosing({ refreshToken: session.refreshToken, familyName: session.familyName ?? "", roster });
      return;
    }
    const needs = grant.naming.needs;
    naming.current = grant.naming;
    storeGrant(grant);
    settle(needs === undefined ? { type: "naming" } : { type: "naming", needs });
  };

  /**
   * The returning prompt's Log out: ends the resumed session and, with it, this device's keep, showing busy on
   * the button until the gateway answers or gives up, then shows the log in form. One that did not finish says
   * so and offers Retry first (`logout-retry.tsx`).
   */
  const leave = (): void => {
    if (back === null || leaving) {
      return;
    }
    setLeaving(true);
    void logOutWithRetry(base, tokenOf(back.grant), retryLogout).then(() => {
      setLeaving(false);
      setBack(null);
      dispatch({ type: "returned", text: null });
    });
  };

  /**
   * The mode link: between log in and register, or the name step's Log out, which forgets the grant, ends
   * its session and returns to log in, once the logout has finished or the player has left it unfinished
   * through the retry dialog.
   */
  const switchMode = (): void => {
    if (waiting.current) {
      return;
    }
    const leaving = state.mode === "naming" ? naming.current : null;
    if (leaving === null) {
      dispatch({ type: "switched" });
      return;
    }
    naming.current = null;
    storeGrant(null);
    waiting.current = true;
    void logOutWithRetry(base, leaving.refreshToken, retryLogout).then(() => {
      waiting.current = false;
      dispatch({ type: "switched" });
    });
  };

  /**
   * The name step's Log out everywhere: asks through the confirm dialog and, once every session has ended,
   * returns to log in as Log out does. When the gateway answers that this tab's own session is already gone,
   * the tab forgets its grant and, once the player has closed the dialog, returns to log in with the
   * gateway's sentence.
   */
  const everywhere = (): void => {
    const held = naming.current;
    if (waiting.current || state.mode !== "naming" || held === null) {
      return;
    }
    let lost: string | null = null;
    void confirmEverywhere(async () => {
      const outcome = await logoutEverywhere(base, held.refreshToken);
      if (!outcome.ok && outcome.lost) {
        lost = outcome.message;
        storeGrant(null);
      }
      return outcome;
    }).then((ended) => {
      if (!ended && lost === null) {
        return;
      }
      naming.current = null;
      dispatch({ type: "returned", text: ended ? null : lost });
    });
  };

  const selecting = choosing !== null;
  useEffect(() => {
    pageSound.scene(selecting ? MusicScene.Select : MusicScene.Title);
  }, [selecting]);

  useEffect(() => {
    void looks.source();
  }, [looks]);

  if (back !== null) {
    return (
      <ReturningScreen
        email={back.email}
        busy={leaving}
        onContinue={() => {
          if (leaving) {
            return;
          }
          const grant = back.grant;
          setBack(null);
          proceed(grant);
        }}
        onLogOut={leave}
      />
    );
  }
  if (choosing !== null) {
    return (
      <SelectFlow
        base={base}
        refreshToken={choosing.refreshToken}
        familyName={choosing.familyName}
        roster={choosing.roster}
        confirmEverywhere={confirmEverywhere}
        ask={ask}
        retryLogout={retryLogout}
        looks={looks}
        onSession={onSession}
        onReturned={(text) => {
          setChoosing(null);
          dispatch({ type: "returned", text });
        }}
      />
    );
  }
  if (state.mode === "naming" && state.step === "look" && state.look !== null) {
    return (
      <LookStep
        entry={looks}
        words={lookStepWords()}
        backGlyph="chevron-left"
        look={state.look}
        voice={state.voice}
        saving={state.busy}
        note={state.lookNote?.tone === "info" ? state.lookNote.text : null}
        refusal={state.lookNote?.tone === "error" ? { sentence: state.lookNote.text, row: null } : null}
        onLook={(look) => dispatch({ type: "looked", look })}
        onVoice={(voice) => dispatch({ type: "voiced", voice })}
        onPreview={() => {
          if (!pageSound.previewVoice(state.voice)) {
            dispatch({ type: "told", text: say("account.voice.noSample") });
          }
        }}
        onBack={() => {
          if (!waiting.current) {
            dispatch({ type: "unlooked" });
          }
        }}
        onPrimary={submit}
      />
    );
  }
  return (
    <LoginScreen
      state={state}
      inputs={inputs}
      blocked={status !== null && loginBlocked(view, Date.now())}
      onType={(field, value) => dispatch({ type: "typed", field, value })}
      onKeep={(value) => dispatch({ type: "kept", value })}
      onLeave={(field) => dispatch({ type: "left", field })}
      onReveal={() => dispatch({ type: "reveal" })}
      onSubmit={submit}
      onSwitch={switchMode}
      onLogOutEverywhere={everywhere}
    />
  );
}

/**
 * What the login screen opens on and what it needs from the page: the naming grant this tab holds, or null to open
 * on the log in form; what a resume of this browser's kept session gave, or null; the account to open character
 * select for, or null, as the game menu's Change character opens it with the kept session's refresh (P1.9-T62); the
 * confirmation before logging out everywhere; the page's confirm dialog, which character select's Log out asks in
 * first; where the screen writes the refresh token it holds while it is up; the dialog for a logout that did not
 * finish; the server's status poll, whose strip the screens show, or null for none (ADR-078); and the looks the
 * Look step and character select draw (ADR-094 decisions 23 and 27).
 */
export interface LoginOptions {
  readonly pending?: Naming | null;
  readonly returning?: Returning | null;
  readonly choosing?: Choosing | null;
  readonly confirmEverywhere: ConfirmLogoutEverywhere;
  readonly ask?: Ask;
  readonly held?: HeldToken;
  readonly retryLogout?: RetryLogout;
  readonly status?: StatusPoll | null;
  readonly looks: EntryLooks;
}

/**
 * Shows the login screen and resolves once a character may play. Registering does not log in by itself: the
 * screen returns to the log in form with the new account's email filled in. A login answered with a naming
 * grant moves to the name step, and that grant is stored so a reload returns to the step; a page that already
 * holds a naming grant opens on the step. A login answered with the account's characters moves to character
 * select, and nothing is stored until a character is chosen, so a reload before choosing returns to the log
 * in form; a page that names an account to choose for opens on character select. The name step's and character
 * select's Log out everywhere asks through `confirmEverywhere` and, once every session has ended, returns to the
 * log in form as Log out does; when the gateway answers that this tab's own session is already gone, the screen
 * returns to the log in form with the gateway's sentence once the player closes the dialog. What a resume gave a
 * tab opens the screen on the returning prompt instead. Once a character may play, the screen leaves the page and
 * the promise resolves.
 *
 * @param base The account origin.
 * @param options What the screen opens on and what it needs from the page (`LoginOptions`); `ask` confirms at
 *   once unless given, and `retryLogout` gives up at once.
 * @returns The session.
 * @throws Error when the page has no element with id `login` to hold the screen.
 */
export function showLogin(base: string, options: LoginOptions): Promise<Session> {
  const host = document.getElementById("login");
  if (host === null) {
    throw new Error("the login screen needs an element with id login");
  }
  const held = options.held ?? { refreshToken: null };
  const root = createRoot(host);
  host.hidden = false;
  return new Promise<Session>((resolve) => {
    /**
     * Takes the screen off the page and hands the session to the page.
     *
     * @param session The session.
     */
    const finish = (session: Session): void => {
      root.unmount();
      host.hidden = true;
      held.refreshToken = null;
      resolve(session);
    };
    root.render(
      <LoginFlow
        base={base}
        pending={options.pending ?? null}
        returning={options.returning ?? null}
        choosing={options.choosing ?? null}
        confirmEverywhere={options.confirmEverywhere}
        ask={options.ask ?? ASK_NOTHING}
        held={held}
        retryLogout={options.retryLogout ?? (() => Promise.resolve(false))}
        status={options.status ?? null}
        looks={options.looks}
        onSession={finish}
      />,
    );
  });
}
